'use client';

import { Box, Typography } from '@/primitives';

const sectionStyle = { mb: 2 };
const headingStyle = { fontWeight: 'bold', mb: 1, mt: 3 };
const subHeadingStyle = { fontWeight: 'bold', mb: 1, mt: 2 };

interface PrivacyPolicyContentProps {
  /** When true, applies page-level max-width + outer padding (use on standalone routes). */
  page?: boolean;
}

export function PrivacyPolicyContent({
  page = false,
}: PrivacyPolicyContentProps = {}) {
  return (
    <Box
      sx={{
        color: 'text.primary',
        ...(page
          ? {
              width: '100%',
              maxWidth: { xs: '100%', md: 1000 },
              mx: 'auto',
              px: { xs: 2, md: 4 },
              py: { xs: 2, md: 4 },
            }
          : { px: 2, py: 1 }),
      }}
    >
      <Typography variant="headlinesmall" sx={{ mb: 1, fontWeight: 'bold' }}>
        Privacy Policy
      </Typography>
      <Typography variant="bodysmall" sx={{ mb: 2 }}>
        Effective Date: 1 January 2024
      </Typography>

      {/* 1. Introduction */}
      <Typography variant="titlemedium" sx={headingStyle}>
        1. Introduction
      </Typography>
      <Typography variant="bodysmall" sx={sectionStyle}>
        Agricultural Business Research Institute (ABRI, &lsquo;we&rsquo;,
        &lsquo;our&rsquo; or &lsquo;us&rsquo;) is committed to protecting your
        privacy. This Privacy Policy explains how we collect, use, disclose, and
        safeguard your information when you visit our website breedlogic.com.au,
        including any other media form, media channel, mobile website, or mobile
        application related or connected thereto (collectively, the
        &lsquo;Site&rsquo;).
      </Typography>

      {/* 2. Information We Collect */}
      <Typography variant="titlemedium" sx={headingStyle}>
        2. Information We Collect
      </Typography>
      <Typography variant="titlesmall" sx={subHeadingStyle}>
        Personal Data
      </Typography>
      <Typography variant="bodysmall" sx={{ mb: 1 }}>
        Personally identifiable information, such as your name, shipping
        address, email address, and telephone number, and demographic
        information, such as your age, gender, hometown, and interests, that you
        voluntarily provide when you register with the Site, make a purchase, or
        participate in various activities related to the Site.
      </Typography>
      <Typography variant="titlesmall" sx={subHeadingStyle}>
        Derivative Data
      </Typography>
      <Typography variant="bodysmall" sx={{ mb: 1 }}>
        Information our servers automatically collect when you access the Site,
        such as your IP address, browser type, operating system, access times,
        and the pages you have viewed directly before and after accessing the
        Site.
      </Typography>
      <Typography variant="titlesmall" sx={subHeadingStyle}>
        Financial Data
      </Typography>
      <Typography variant="bodysmall" sx={sectionStyle}>
        Financial information, such as payment details (e.g., credit card
        number, card brand, expiration date) collected when you purchase, order,
        return, exchange, or request information about our services.
      </Typography>

      {/* 3. Use of Your Information */}
      <Typography variant="titlemedium" sx={headingStyle}>
        3. Use of Your Information
      </Typography>
      <Box component="ul" sx={{ pl: 3, mb: 2, '& li': { mb: 0.5 } }}>
        <li>
          <Typography variant="bodysmall">
            Create and manage your account.
          </Typography>
        </li>
        <li>
          <Typography variant="bodysmall">
            Process transactions and send purchase confirmations and invoices.
          </Typography>
        </li>
        <li>
          <Typography variant="bodysmall">
            Respond to your inquiries and provide customer support.
          </Typography>
        </li>
        <li>
          <Typography variant="bodysmall">
            Send technical notices, security alerts, and administrative
            messages.
          </Typography>
        </li>
        <li>
          <Typography variant="bodysmall">
            Communicate with you about products, services, offers, promotions,
            and events.
          </Typography>
        </li>
        <li>
          <Typography variant="bodysmall">
            Monitor trends, usage, and improve our Services.
          </Typography>
        </li>
        <li>
          <Typography variant="bodysmall">
            Facilitate SSO (Single Sign-On) and related services.
          </Typography>
        </li>
      </Box>

      {/* 4. Single Sign-On (SSO) */}
      <Typography variant="titlemedium" sx={headingStyle}>
        4. Single Sign-On (SSO)
      </Typography>
      <Typography variant="bodysmall" sx={sectionStyle}>
        We offer SSO services via third-party services like Microsoft and
        Facebook. We collect and use only the necessary authentication
        information.
      </Typography>

      {/* 5. Sharing Your Information */}
      <Typography variant="titlemedium" sx={headingStyle}>
        5. Sharing Your Information
      </Typography>
      <Box sx={sectionStyle}>
        <Typography variant="bodysmall" sx={{ mb: 0.5 }}>
          <strong>By Law or to Protect Rights:</strong> If required by legal
          obligations.
        </Typography>
        <Typography variant="bodysmall" sx={{ mb: 0.5 }}>
          <strong>Business Transfers:</strong> In case of mergers, acquisitions,
          or asset sales.
        </Typography>
        <Typography variant="bodysmall" sx={{ mb: 0.5 }}>
          <strong>Third-Party Service Providers:</strong> For payment
          processing, hosting, customer service, and marketing assistance.
        </Typography>
      </Box>

      {/* 6. Security of Your Information */}
      <Typography variant="titlemedium" sx={headingStyle}>
        6. Security of Your Information
      </Typography>
      <Typography variant="bodysmall" sx={sectionStyle}>
        We implement administrative, technical, and physical security measures
        to protect your personal information. However, no security system is
        impenetrable.
      </Typography>

      {/* 7. Policy for Children */}
      <Typography variant="titlemedium" sx={headingStyle}>
        7. Policy for Children
      </Typography>
      <Typography variant="bodysmall" sx={sectionStyle}>
        We do not knowingly collect data from children under 13. If you believe
        we have, please contact us for removal.
      </Typography>

      {/* 8. Data Retention and Deletion of Personal Information */}
      <Typography variant="titlemedium" sx={headingStyle}>
        8. Data Retention and Deletion of Personal Information
      </Typography>
      <Typography variant="bodysmall" sx={sectionStyle}>
        We retain personal information only as long as necessary. You may
        request deletion by contacting office@abri.une.edu.au.
      </Typography>

      {/* 9. Changes to This Privacy Policy */}
      <Typography variant="titlemedium" sx={headingStyle}>
        9. Changes to This Privacy Policy
      </Typography>
      <Typography variant="bodysmall" sx={sectionStyle}>
        We may update this Privacy Policy periodically. Changes will be posted
        on this page.
      </Typography>

      {/* 10. Contact Us */}
      <Typography variant="titlemedium" sx={headingStyle}>
        10. Contact Us
      </Typography>
      <Typography variant="bodysmall" sx={{ mb: 1 }}>
        If you have questions or comments about this Privacy Policy, please
        contact us:
      </Typography>
      <Box sx={sectionStyle}>
        <Typography variant="bodysmall" sx={{ fontWeight: 'bold' }}>
          ABRI
        </Typography>
        <Typography variant="bodysmall">UNE, The Short Run</Typography>
        <Typography variant="bodysmall">Armidale, NSW, 2351</Typography>
        <Typography variant="bodysmall">Australia</Typography>
        <Typography variant="bodysmall">Tel: +61 2 6773 3555</Typography>
        <Typography variant="bodysmall">
          Email: office@abri.une.edu.au
        </Typography>
      </Box>
    </Box>
  );
}
